import { useAuth } from "@hooks/useAuth";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import { useDataQuery } from "@hooks/useData/useDataQuery";
import { selectData, softDelete, supabase } from "@utils/serverClient";
import { App, InsertApp, UpdateApp } from "types";
import { v4 as uuid } from "uuid";

export function useApps() {
  const { user } = useAuth();
  const queryKey = ["apps"];
  const userAppsQueryKey = ["getUserApps"];

  const { data, status, refetch } = useDataQuery<App[]>({
    queryKey: queryKey,
    queryFn: async () => {
      const { data, error } = await selectData({
        table: "app",
      })
        .eq("show_listing", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useDataMutation({
    mutationFn: async (formData: InsertApp) => {
      const { data, error } = await supabase
        .from("app")
        .insert(formData)
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to add app.",
    optimisticUpdateFn: (oldData: App[] | undefined, newApp) => {
      const optimisticApp: App = {
        ...newApp,
        deleted: false,
        show_listing: true,
        updated_at: new Date().toISOString(),
        id: uuid(), // Temporary ID
        created_at: new Date().toISOString(),
      };
      return [optimisticApp, ...(oldData || [])];
    },
  });

  const updateMutation = useDataMutation({
    mutationFn: async (formData: UpdateApp) => {
      const { data, error } = await supabase
        .from("app")
        .update(formData)
        .match({ id: formData.id })
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update app.",
    optimisticUpdateFn: (oldData: App[] | undefined, updatedApp) => {
      if (!oldData) return [];
      return oldData.map((app) =>
        app.id === updatedApp.id ? { ...app, ...updatedApp } : app,
      );
    },
  });

  const deleteMutation = useDataMutation({
    mutationFn: async (id) => {
      const { error } = await softDelete({
        table: "app",
      }).match({ id });
      if (error) throw new Error(error.message);
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to delete app.",
    optimisticUpdateFn: (oldData: App[] | undefined, id) => {
      return oldData?.filter((app) => app.id !== id) ?? [];
    },
  });

  const installAppMutation = useDataMutation({
    mutationFn: async (appId: App["id"]) => {
      if (!user?.id) throw new Error("User not authenticated");
      const { error } = await supabase.from("profile_app").upsert({
        app_id: appId,
        user_id: user.id,
      });
      if (error) throw error;
      return { app_id: appId, user_id: user.id };
    },
    queryKeyToInvalidate: userAppsQueryKey,
    errorMessage: "Failed to install app.",
    optimisticUpdateFn: (oldData: string[] | undefined, appId) => {
      return [...(oldData || []), appId];
    },
  });

  const uninstallAppMutation = useDataMutation({
    mutationFn: async (appId) => {
      if (!user?.id) throw new Error("User not authenticated");
      const { error } = await supabase
        .from("profile_app")
        .delete()
        .match({
          user_id: user.id,
          app_id: appId,
        });
      if (error) throw error;
    },
    queryKeyToInvalidate: userAppsQueryKey,
    errorMessage: "Failed to uninstall app.",
    optimisticUpdateFn: (oldData: App["id"][] | undefined, appId) => {
      return oldData?.filter((id) => id !== appId) ?? [];
    },
  });

  return {
    apps: data ?? [],
    appsFetchStatus: status,
    getApps: refetch,
    createApp: createMutation.mutate,
    isCreatingApp: createMutation.isPending,
    updateApp: updateMutation.mutate,
    isUpdatingApp: updateMutation.isPending,
    deleteApp: deleteMutation.mutate,
    isDeletingApp: deleteMutation.isPending,
    installApp: installAppMutation.mutate,
    isInstallingApp: installAppMutation.isPending,
    uninstallApp: uninstallAppMutation.mutate,
    isUninstallingApp: uninstallAppMutation.isPending,
  };
}